import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TimerProvider } from './context/TimerContext';
import { WorkspaceProvider, useWorkspace } from "./context/WorkspaceContext";
import { NotificationProvider } from "./context/NotificationContext";
import { DailyStandupPopup } from "./components/DailyStandupPopup";

// Inner component that has access to workspace context
const DailyStandupPopupWrapper = () => {
    const { currentWorkspace } = useWorkspace();
    if (!currentWorkspace?.id) return null;
    // Opt-in per workspace — default nonaktif, jangan sampai popup-status ikut dipanggil
    if (!currentWorkspace.daily_standup_enabled) return null;
    return (
        <DailyStandupPopup
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
            companyName={currentWorkspace.company_name}
        />
    );
};

// Wrapper that provides both Workspace and Notification context together.
// NotificationProvider calls useWorkspace(), so it MUST be inside WorkspaceProvider.
const WorkspaceLayout = ({ children }) => (
    <WorkspaceProvider>
        <NotificationProvider>
            {children}
            <DailyStandupPopupWrapper />
        </NotificationProvider>
    </WorkspaceProvider>
);

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import MyTasksPage from "./pages/MyTasksPage";
import ReviewByMePage from "./pages/ReviewByMePage";
import AssignByMePage from "./pages/AssignByMePage";
import CompanyManagementPage from "./pages/CompanyManagementPage";
import TasksPage from "./pages/TasksPage";
import GoalsPage from "./pages/GoalsPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import TeamPage from "./pages/TeamPage";
import SettingsPage from "./pages/SettingsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
// import ClickUpMigrationPage from "./pages/ClickUpMigrationPage";
import DeletedTasksPage from "./pages/DeletedTasksPage";
import ShareTaskPage from "./pages/ShareTaskPage";

import CalendarPage from "./pages/CalendarPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import LeadsPage from "./pages/LeadsPage";
import PMClientsPage from "./pages/PMClientsPage";
import ProjectsPage from "./pages/ProjectsPage";
import CrmPartnershipsPage from "./pages/crm/PartnershipsPage";
import CrmLeadsPage from "./pages/crm/CrmLeadsPage";
import CrmClientsPage from "./pages/crm/ClientsPage";
import CrmProjectsPage from "./pages/crm/CrmProjectsPage";
import AIAgentsPage from "./pages/AIAgentsPage";
import FileManagerPage from "./pages/FileManagerPage";
import TrackingMemberPage from "./pages/TrackingMemberPage";
import DiscussionPage from "./pages/DiscussionPage";
import DirectMessagesPage from "./pages/DirectMessagesPage";
import ZoomSettingsPage from "./pages/ZoomSettingsPage";
import APIKeyManagementPage from "./pages/APIKeyManagementPage";
import SpaceSettingsPage from "./pages/SpaceSettingsPage";
import ZimbraSettingsPage from "./pages/ZimbraSettingsPage";
import GmailLikeMailPage from "./pages/GmailLikeMailPage";
import FilesPage from "./pages/FilesPage";
import OidcAuthorizePage from "./pages/OidcAuthorizePage";
import GuestMessagesPage from "./pages/GuestMessagesPage";
import DailyStandupPage from "./pages/DailyStandupPage";
import StandupPlanPage from "./pages/StandupPlanPage";

// SuperAdmin Pages
import SuperAdminLogin from "./pages/superadmin/SuperAdminLogin";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import UserManager from "./pages/superadmin/UserManager";
import WorkspaceManager from "./pages/superadmin/WorkspaceManager";
import CompanyManager from "./pages/superadmin/CompanyManager";
import MobileTokenManager from "./pages/superadmin/MobileTokenManager";
import OpenClawTokenManager from "./pages/superadmin/OpenClawTokenManager";
import AgentManager from "./pages/superadmin/AgentManager";
import AgentConfigPanel from "./pages/superadmin/AgentConfigPanel";
import ApiDocsManager from "./pages/superadmin/ApiDocsManager";
import UserDocsManager from "./pages/superadmin/UserDocsManager";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-brand-background">
                <div className="spinner" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Admin Route Component - Only for admin/owner
const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const { currentUserRole } = useWorkspace();
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-brand-background">
                <div className="spinner" />
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Allow access for owner and admin roles, or if role hasn't been loaded yet
    if (currentUserRole && currentUserRole !== 'owner' && currentUserRole !== 'admin') {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-brand-background">
                <div className="spinner" />
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

// Main App Routes
const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/share/task/:taskId" element={<ShareTaskPage />} />
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route 
                path="/register" 
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                } 
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Protected Routes */}
            <Route 
                path="/" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <DashboardPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/my-tasks" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <MyTasksPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/review-by-me" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <ReviewByMePage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assigned-by-me" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <AssignByMePage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/company-management" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CompanyManagementPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/guest-messages" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <GuestMessagesPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/zoom-settings" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <ZoomSettingsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/zimbra-settings" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <ZimbraSettingsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/api-keys" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <APIKeyManagementPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/mail/inbox" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <GmailLikeMailPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/files" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <FilesPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route path="/oidc/authorize" element={<OidcAuthorizePage />} />
            <Route 
                path="/tasks" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <TasksPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/goals" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <GoalsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/time" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <TimeTrackingPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/team" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <TeamPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/calendar" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CalendarPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/space/:spaceId/dashboard" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <ClientDashboardPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/space/:spaceId/files" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <FileManagerPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/space/:spaceId/settings" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <SpaceSettingsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/pm/leads" 
                element={<Navigate to="/crm/leads" replace />}
            />
            <Route 
                path="/pm/clients" 
                element={<Navigate to="/crm/clients" replace />}
            />
            <Route 
                path="/pm/projects" 
                element={<Navigate to="/crm/projects" replace />}
            />
            <Route 
                path="/crm/partnerships" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CrmPartnershipsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/crm/leads" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CrmLeadsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/crm/clients" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CrmClientsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/crm/projects" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <CrmProjectsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ai-agents" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <AIAgentsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Admin Routes */}
            <Route 
                path="/admin/dashboard" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <AdminDashboardPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/deleted-tasks" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <DeletedTasksPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/settings" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <SettingsPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/tracking-member" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <TrackingMemberPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Discussion Route */}
            <Route 
                path="/discussion" 
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <DiscussionPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                } 
            />

            {/* Direct Messages Route */}
            <Route
                path="/direct-messages"
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <DirectMessagesPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                }
            />

            {/* Daily Standup Route */}
            <Route
                path="/daily-standup"
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <DailyStandupPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                }
            />

            {/* Daily Standup — Buat Rencana */}
            <Route
                path="/daily-standup/plan"
                element={
                    <ProtectedRoute>
                        <WorkspaceLayout>
                            <StandupPlanPage />
                        </WorkspaceLayout>
                    </ProtectedRoute>
                }
            />

            {/* Legacy settings route redirect */}
            <Route 
                path="/settings" 
                element={<Navigate to="/admin/settings" replace />} 
            />

            {/* SuperAdmin Routes - login must be OUTSIDE the layout to avoid redirect loop */}
            <Route path="/superadmin/login" element={<SuperAdminLogin />} />
            <Route path="/superadmin" element={<SuperAdminLayout />}>
                <Route index element={<SuperAdminDashboard />} />
                <Route path="users" element={<UserManager />} />
                <Route path="workspaces" element={<WorkspaceManager />} />
                <Route path="companies" element={<CompanyManager />} />
                <Route path="mobile-tokens" element={<MobileTokenManager />} />
                <Route path="openclaw-tokens" element={<OpenClawTokenManager />} />
                <Route path="ai-agents" element={<AgentManager />} />
                <Route path="ai-agents/:agentId" element={<AgentConfigPanel />} />
                <Route path="api-docs" element={<ApiDocsManager />} />
                <Route path="user-docs" element={<UserDocsManager />} />
            </Route>
            
            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = "18356370171-c25a8fjpm6gdmvm9vbiegvqt3ge2gtpi.apps.googleusercontent.com";

function App() {
    return (
        <div className="App min-h-screen bg-brand-background">
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <BrowserRouter>
                    <AuthProvider>
                        <TimerProvider>
                            <AppRoutes />
                            <Toaster 
                                position="top-right" 
                                toastOptions={{
                                    className: 'sonner-toast',
                                    style: {
                                        zIndex: 99999,  // Paling atas, above modal
                                    },
                                    // Custom styling per type
                                    classNames: {
                                        error: 'sonner-error-custom',
                                        success: 'sonner-success-custom',
                                        warning: 'bg-yellow-50 border-yellow-200 text-yellow-900',
                                        info: 'bg-blue-50 border-blue-200 text-blue-900',
                                    },
                                }}
                                />
                        </TimerProvider>
                    </AuthProvider>
                </BrowserRouter>
            </GoogleOAuthProvider>
        </div>
    );
}

export default App;
